import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { centroid } from '@turf/turf';
import type { Polygon, MultiPolygon } from 'geojson';

import { Badge } from '@/components/ui/badge';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { zoomMapViewToLocation } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/mapViewZoom';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { resolveSitrepDefaultSpatialFilter } from 'components/Reports/utils/resolveSitrepSpatialFilter';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';

/** Regional areas stay recognizable around their center. */
const REPORT_SCOPE_CENTROID_ZOOM = 6;

type ScopeSitrep = Pick<GetSitrepsResponse, 'id' | 'aor_id' | 'incident_id'>;

function polygonCentroid(
  geometry: Polygon | MultiPolygon
): [number, number] | null {
  try {
    const point = centroid(geometry);
    const [lng, lat] = point.geometry.coordinates;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    return [lng, lat];
  } catch {
    return null;
  }
}

function useReportScope(sitrep: ScopeSitrep | null | undefined) {
  const { data: aors = [] } = useAors();
  const { data: incidents = [] } = useAllIncidents();
  return useMemo(() => {
    if (!sitrep) return null;
    const incident = sitrep.incident_id
      ? incidents.find((item) => item.id === sitrep.incident_id)
      : null;
    const aor = sitrep.aor_id
      ? aors.find((item) => item.id === sitrep.aor_id)
      : null;
    return resolveSitrepDefaultSpatialFilter({ sitrep, incident, aor });
  }, [aors, incidents, sitrep]);
}

/** Zoom once per report, when its area polygon and the map are both ready. */
export function useZoomToReportScope(sitrep: ScopeSitrep | null | undefined) {
  const scope = useReportScope(sitrep);
  const mapCtx = useOptionalUscgMapContext();
  const mapView = mapCtx?.mapView ?? null;
  const mapVisible = mapCtx?.isMapVisible ?? false;
  const zoomedKeyRef = useRef<string | null>(null);
  const attemptKeyRef = useRef<string | null>(null);
  const zoomAttemptsRef = useRef(0);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    if (!mapVisible || !sitrep || !scope || !mapView) return;
    const key = `${sitrep.id}:${scope.spatialFilterLabel}`;
    if (zoomedKeyRef.current === key) return;
    const center = polygonCentroid(scope.spatialFilter);
    if (!center) return;
    if (attemptKeyRef.current !== key) {
      attemptKeyRef.current = key;
      zoomAttemptsRef.current = 0;
    }
    zoomedKeyRef.current = key;
    void zoomMapViewToLocation({
      mapView,
      longitude: center[0],
      latitude: center[1],
      zoom: REPORT_SCOPE_CENTROID_ZOOM,
    })
      ?.then(() => {
        const lng = mapView.center?.longitude;
        const lat = mapView.center?.latitude;
        const arrived =
          lng != null &&
          lat != null &&
          Math.abs(lng - center[0]) < 0.5 &&
          Math.abs(lat - center[1]) < 0.5 &&
          Math.abs((mapView.zoom ?? 0) - REPORT_SCOPE_CENTROID_ZOOM) < 0.5;
        if (arrived || zoomAttemptsRef.current >= 3) return;
        zoomedKeyRef.current = null;
        zoomAttemptsRef.current += 1;
        setRetryTick((tick) => tick + 1);
      })
      ?.catch(() => {
        zoomedKeyRef.current = null;
        if (zoomAttemptsRef.current >= 3) return;
        zoomAttemptsRef.current += 1;
        setRetryTick((tick) => tick + 1);
      });
  }, [mapView, mapVisible, retryTick, scope, sitrep]);

  return scope;
}

export function PresentedReportFrame({
  sitrep,
  children,
}: {
  sitrep: GetSitrepsResponse;
  children: ReactNode;
}) {
  const aorText = useAorText();
  const scope = useZoomToReportScope(sitrep);

  return (
    <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-col">
      <div className="tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:gap-2 tw:border-b tw:border-border tw:bg-card tw:px-3 tw:py-3.5">
        {sitrep.name ? (
          <span className="tw:text-base tw:font-medium">{sitrep.name}</span>
        ) : null}
        {scope ? (
          <Badge variant="outline">{aorText(scope.spatialFilterLabel)}</Badge>
        ) : null}
      </div>
      <div className="tw:min-h-0 tw:flex-1 tw:overflow-hidden tw:pt-4">
        {children}
      </div>
    </div>
  );
}
